import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '../components/icons';
import { Mark } from '../components/Layout';
import { ErrorNote } from '../components/ui';
import { get, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLoad } from '../lib/hooks';
import { GlobeLanguage } from './Login';

/** Describes the other device from its browser string, e.g. "Chrome on Windows" */
function describe(ua: string | null) {
  if (!ua) return '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  const os = /iPad/.test(ua) ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' · ');
}

/** On the phone that's signed in: approve signing in the screen that showed the QR */
export function QrApprove() {
  const { code = '' } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, error } = useLoad(() => get<{ userAgent: string | null }>(`/api/auth/qr/${encodeURIComponent(code)}`), [code]);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const approve = async () => {
    setBusy(true);
    setErr(null);
    try {
      await post(`/api/auth/qr/${encodeURIComponent(code)}/approve`);
      setDone(true);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login login-v2">
      <div className="login-lang">
        <GlobeLanguage />
      </div>
      <section className="login-card qr-approve">
        <div className="login-brand login-brand-center">
          <Mark />
          <h1>{t('auth.qrApproveTitle')}</h1>
        </div>
        {error ? (
          <>
            <ErrorNote error={error} />
            <Link to="/" className="btn btn-block">
              {t('common.close')}
            </Link>
          </>
        ) : done ? (
          <>
            <p className="qr-done">
              <Icon name="shield" size={28} />
              {t('auth.qrApproved')}
            </p>
            <Link to="/" className="btn btn-block">
              {t('common.close')}
            </Link>
          </>
        ) : data ? (
          <div className="stack">
            <p className="login-lede">{t('auth.qrApproveText', { name: user?.fullName })}</p>
            {describe(data.userAgent) && (
              <p className="qr-device">
                <Icon name="phone" size={20} />
                {describe(data.userAgent)}
              </p>
            )}
            <p className="error-note qr-warning">{t('auth.qrWarning')}</p>
            <ErrorNote error={err} />
            <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void approve()}>
              {t('auth.qrApprove')}
            </button>
            <Link to="/" className="btn btn-block">
              {t('common.cancel')}
            </Link>
          </div>
        ) : (
          <p className="muted">{t('common.loading')}</p>
        )}
      </section>
    </div>
  );
}
